import React, { createContext, useContext } from 'react';
import type {
    BackgroundOptionItem,
    FitSizes,
    ImageEditorCopy,
    UploadError,
} from '../types';
import type { BackgroundSelection, PreparedImage } from '../utils/convertImage';

export type ImageEditorContextValue = {
    copy: ImageEditorCopy;
    aspectRatio: number;
    avatar: boolean;
    fileName: string;
    processingLabel: string;
    isBusy: boolean;
    isUploading: boolean;
    uploadProgress: number;
    uploadError?: UploadError;
    containerRef: React.RefObject<HTMLDivElement>;
    fitSizes: FitSizes;
    imageData: PreparedImage | null;
    isRotating: boolean;
    offset: { x: number; y: number };
    rotation: number;
    zoom: number;
    backgroundSelection: BackgroundSelection;
    backgroundOptions: BackgroundOptionItem[];
    hideBackgroundSelection: boolean;
    isBackgroundVisible: boolean;
    sliderValue: number;
    isResetVisible: boolean;
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    onBackgroundChange: (value: BackgroundSelection, index: number) => void;
    onRotate: () => void;
    onZoomChange: (value: number) => void;
    onZoomMinus: () => void;
    onZoomPlus: () => void;
    onApply: () => void;
    onClose: () => void;
    onReset: () => void;
    name: string;
};

const ImageEditorContext = createContext<ImageEditorContextValue | null>(null);

export const ImageEditorProvider: React.FC<{
    value: ImageEditorContextValue;
    children: React.ReactNode;
}> = ({ value, children }) => (
    <ImageEditorContext.Provider value={value}>
        {children}
    </ImageEditorContext.Provider>
);

export const useImageEditorContext = () => {
    const context = useContext(ImageEditorContext);
    if (!context) {
        throw new Error(
            'useImageEditorContext must be used within ImageEditorProvider',
        );
    }
    return context;
};
